import { Model as BaseModel } from 'react-native-jala-orm';
import _ from 'lodash';
import MassAssignmentError from 'react-native-jala-orm/src/model/MassAssignmentError';
import SQLite from "react-native-sqlite-storage";
import Database from 'crane-query-builder/src/Database';
import ConnectionResolver from 'react-native-jala-orm/src/ConnectionResolver';
import RNSQLiteConnection from 'react-native-jala-orm/src/RNSQLiteConnection';
import Dispatcher from './Dispatcher';
import { Platform } from "react-native"
import WebConnection from "./connection/WebConnection"

SQLite.enablePromise(false)

/**
 * @static {(name: string, callback: Function) => void} addGlobalScope
 */
class Model extends BaseModel {
  static _dispatcher = new Dispatcher()

  /** @var {Array} searchables */
  static searchables = []

  getRelationValue(key) {
    // If the key already exists in the relationships array, it just means the
    // relationship has already been loaded, so we'll just return it out of
    // here because there is no need to query within the relations twice.
    if (this.relationLoaded(key)) {
      return this._relations[key];
    }

    // If the "attribute" exists as a method on the model, we will just assume
    // it is a relationship and will load and return results from the query
    // and hydrate the relationship's value on the "relationships" array.
    const relationMethod = `relation${_.upperFirst(_.camelCase(key))}`;
    if (relationMethod in this && typeof this[relationMethod] === 'function') {
      return this._getRelationshipFromMethod(relationMethod);
    }
    return undefined;
  }

  /**
   * Fill the model with an array of attributes.
   *
   * @return {Model}
   *
   * @throws MassAssignmentException
   * @param {Object} attributes
   */
  fill(attributes) {
    const totallyGuarded = this.totallyGuarded();

    _.forIn(this._fillableFromArray(attributes), (value, key) => {
      const _key = this._removeTableFromKey(key);

      // The developers may choose to place some attributes in the "fillable" array
      // which means only those attributes may be set through mass assignment to
      // the model, and all others will just get ignored for security reasons.
      if (this.isFillable(_key)) {
        this.setAttribute(_key, value);
      } else if (totallyGuarded) {
        throw new MassAssignmentError(_key);
      }
    });

    return this;
  }

  getDirty() {
    const dirty = {};

    _.forIn(this.getAttributes(), (value, key) => {
      if (!this._originalIsEquivalent(key, value)) {
        dirty[key] = value;
      }
    });

    return dirty;
  }

  /**
   * Determine whether an attribute should be cast to a native type.
   *
   * @return {Boolean}
   * @param {String} key
   * @param {Array|String|null} types
   */
  hasCast(key, types = null) {
    if (key in this.getCasts()) {
      return types ? types.includes(this._getCastType(key)) : true;
    }

    return false;
  }

  static _registerModelEvent(event, callback) {
    if (this._dispatcher) {
      const { name } = this;

      this._dispatcher.listen(`model.${event}: ${name}`, callback);
    }
  }

  /**
   * Fire the given event for the model.
   *
   * @return boolean
   * @param {String} event
   * @param {Boolean} halt
   */
  _fireModelEvent(event, halt = true) {
    if (!this.constructor._dispatcher) {
      return true;
    }

    // First, we will get the proper method to call on the event dispatcher, and then we
    // will attempt to fire a custom, object based event for the given event. If that
    // returns a result we can return that result, or we'll call the string events.
    const method = halt ? 'until' : 'fire';

    const result = this._filterModelEventResults(
      this._fireCustomModelEvent(event, method)
    );

    if (result === false) {
      return false;
    }

    return result || (this.constructor._dispatcher && this.constructor._dispatcher[method](
      `model.${event}: ${this.constructor.name}`, this
    ));
  }

  /**
   * Resolve a connection instance.
   *
   * @return {Connection}
   * @param {String} connection
   */
  static resolveConnection(connection = null) {
    if (!this._resolver) {
      if (Platform.OS === 'web') {
        this._resolver = new ConnectionResolver({
          'rn-sqlite': new WebConnection()
        });
      } else {
        RNSQLiteConnection.prototype.run = function run({ sql, params, resolve, reject }) {
          return this.executeSql(
            sql, params,
            resolve || ((res) => res),
            reject || Model.sqlErrorHandler
          );
        };
        this._resolver = new ConnectionResolver({
          'rn-sqlite': new RNSQLiteConnection()
        });
        Database.database.executeSql('PRAGMA analysis_limit=400; PRAGMA optimize;');
      }
    }
    return this._resolver.connection(connection);
  }

  static sqlErrorHandler(errors) {
    if (errors instanceof Error) throw errors;
    else if (errors.err && errors.err.message) {
      const error = new Error(errors.err.message, { cause: errors });
      error.name = 'SQLError';
      error.code = errors.err.code;
      throw error;
    }
    throw errors;
  }

  static applyFetchParamsToQuery(query, params, param) {
    if (params[param] === '' || params[param] === null || params[param] === undefined) return query;
    if (param === 'search') {
      if (this.searchables.length > 0) {
        query.where((q) => {
          this.searchables.forEach((column) => {
            q.orWhere(q.qualifyColumn(column), 'like', `%${(params[param] || '').replace(' ', '%')}%`)
          })
        })
      }
    }
    else if (param === 'keys') {
      const keys = `${params[param]}`.split(',')
      query.whereIn(query.qualifyColumn(new this().getKeyName()), keys)
    }
    else if (param === 'sort') {
      const sorts = params[param].split('|').map((order) => order.split(','))
      sorts.forEach((sorts) => {
        query.orderBy(query.qualifyColumn(sorts[0]), sorts[1] || 'asc')
      })
    }
    else if (param.includes('__')) {
      const [column, operation] = param.split('__')
      if (operation === 'eq') {
        query.where(query.qualifyColumn(column), params[param])
      }
      else if (operation === 'ne') {
        query.where(query.qualifyColumn(column), '<>', params[param])
      }
      else if (operation === 'in') {
        const values = `${params[param]}`.split(',')
        query.whereIn(query.qualifyColumn(column), values)
      }
      else if (operation === 'not_in') {
        const values = `${params[param]}`.split(',')
        query.whereNotIn(query.qualifyColumn(column), values)
      }
      else if (['gt', 'gte', 'lt', 'lte'].includes(operation)) {
        const op = {'gt': '>', 'gte': '>=', 'lt': '<', 'lte': '<='}[operation]
        query.where(query.qualifyColumn(column), op, params[param])
      }
      else if (operation === 'null' && params[param]) {
        query.whereNull(query.qualifyColumn(column))
      }
      else if (operation === 'not_null' && params[param]) {
        query.whereNotNull(query.qualifyColumn(column))
      }
    }
    else if (this.searchables.includes(param)) {
      query.where(query.qualifyColumn(param), 'like', `%${(params[param] || '').replace(' ', '%')}%`)
    }
    return query;
  }

  static fetch(params = {}) {
    const query = this.query()
    Object.keys(params || {}).forEach((param) => {
      this.applyFetchParamsToQuery(query, params, param);
    })
    return query.get();
  }

  getFillable() {
    return this._fillable;
  }

  static hydrate(attributes = {}) {
    const model = new this();
    Object.keys(attributes).forEach((k) => {
      model.setAttribute(k, attributes[k])
    })
    model.syncOriginal();
    model.exists = true
    return model;
  }

  static renderSqlWithParams(query) {
    let sql = query.toSql()
    const bindings = query.getBindings();
    if (!_.isEmpty(bindings)) {
      _.forOwn(bindings, (binding, key) => {
        const regex = _.isNumber(key)
          ? /(?=(?:[^'\\]*'[^'\\]*')*[^'\\]*$)/
          : new RegExp(`/:${key}(?=(?:[^'\\\\]*'[^'\\\\]*')*[^'\\\\]*$)/`)

        if (!_.isNumber(binding) && binding instanceof String)
          binding = `'${binding}'`;

        sql = sql.replace(regex, binding)
      })
    }

    return sql;
  }
}

Model.resolveConnection();

export default Model;
